import { Component, signal, computed, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subscription, combineLatest, filter, take } from 'rxjs';
import {
  IconComponent,
  ButtonComponent,
  HeroSectionComponent,
  HeroMetaItem,
  PageNavItem,
  FaqAccordionComponent,
  FaqItem as LibFaqItem,
  CtaSectionComponent,
  ChecklistComponent,
  ChecklistItem,
  InfoCardGridComponent,
  InfoCard,
  SkeletonComponent
} from 'htx-ui-library';
import { AuthService } from '../../../../services/auth/auth.service';
import { RoleService } from '../../../../services/role/role.service';
import { UserRole } from '../../../../services/auth/auth.role';
import { NavigationService } from '../../../../services/navigation.service';
import { ProtectedContentComponent } from '../../../organisms/protected-content/protected-content.component';
import { UseCaseParticipationService } from '../../../../services/use-case-participation/use-case-participation.service';
import { NotificationService } from '../../../../services/form/notification.service';
import { OrganizationService } from '../../../../services/organizations/organization.service';
import {
  AdmissionState,
  ActivityState
} from '../../../../services/organizations/organization.model';

interface Role {
  icon: string;
  title: string;
  description: string;
  permissions: string[];
}

interface SoftwareComponent {
  icon: string;
  iconColor: 'teal' | 'blue' | 'green';
  title: string;
  description: string;
  status: string;
  screenshots: { src: string; caption: string }[];
  groupKey?: string;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface UseCaseDocument {
  titleKey: string;
  url: string;
  filename: string;
}

interface UseCaseData {
  id: string;
  icon: string;
  titleKey: string;
  subtitleKey: string;
  rolesCount: number;
  componentsCount: number;
  descriptionKey: string;
  roles: Role[];
  software: SoftwareComponent[];
  diagram: { src: string; caption: string } | null;
  documents: UseCaseDocument[];
  prerequisites: string[];
  faq: FaqItem[];
}

@Component({
  selector: 'app-use-case-detail-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    IconComponent,
    ButtonComponent,
    HeroSectionComponent,
    FaqAccordionComponent,
    CtaSectionComponent,
    ChecklistComponent,
    InfoCardGridComponent,
    ProtectedContentComponent,
    SkeletonComponent
  ],
  templateUrl: './use-case-detail-page.component.html',
  styleUrl: './use-case-detail-page.component.scss'
})
export class UseCaseDetailPageComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private transloco = inject(TranslocoService);
  private authService = inject(AuthService);
  private roleService = inject(RoleService);
  private navigationService = inject(NavigationService);

  requiredRoles = this.navigationService.getAccessRoles('use-cases');
  private participationService = inject(UseCaseParticipationService);
  private organizationService = inject(OrganizationService);
  private notificationService = inject(NotificationService);
  private translation = toSignal(this.transloco.selectTranslation(), { initialValue: {} });
  private subscriptions = new Subscription();

  useCaseId = signal<string>('');
  isLoggedIn = this.authService.isLoggedIn$;

  private userRoles = toSignal(this.roleService.getUserRoles$(), { initialValue: [] as string[] });

  canParticipate = computed(() => {
    const roles = this.userRoles();
    return (
      roles.includes(UserRole.OrganizationAdmin) ||
      roles.includes(UserRole.OrganizationRepresentative)
    );
  });
  requestInProgress = signal(false);
  participationState = signal<AdmissionState | null>(null);
  orgNotApproved = signal(false);
  orgInactive = signal(false);
  statusLoading = signal(true);
  statusError = signal(false);
  AdmissionState = AdmissionState;

  lightboxSrc = signal<string | null>(null);
  lightboxAlt = signal<string>('');

  useCases: Record<string, UseCaseData> = {
    'supply-chain': {
      id: 'supply-chain',
      icon: 'account_tree',
      titleKey: 'usecases.supplyChain.title',
      subtitleKey: 'usecases.supplyChain.subtitle',
      rolesCount: 3,
      componentsCount: 3,
      descriptionKey: 'usecases.supplyChain.detailDescription',
      diagram: {
        src: 'assets/images/UC 5 Diagramm 0.2.0.svg',
        caption: 'usecases.supplyChain.diagram.caption'
      },
      roles: [
        {
          icon: 'factory',
          title: 'usecases.supplyChain.roles.manufacturer.title',
          description: 'usecases.supplyChain.roles.manufacturer.description',
          permissions: []
        },
        {
          icon: 'local_shipping',
          title: 'usecases.supplyChain.roles.wholesaler.title',
          description: 'usecases.supplyChain.roles.wholesaler.description',
          permissions: []
        },
        {
          icon: 'local_pharmacy',
          title: 'usecases.supplyChain.roles.pharmacy.title',
          description: 'usecases.supplyChain.roles.pharmacy.description',
          permissions: []
        }
      ],
      software: [
        {
          icon: 'sync_alt',
          iconColor: 'teal',
          title: 'usecases.supplyChain.software.integrator.title',
          description: 'usecases.supplyChain.software.integrator.description',
          status: 'usecases.status.inDevelopment',
          screenshots: [],
          groupKey: 'usecases.supplyChain.softwareGroups.provider'
        },
        {
          icon: 'description',
          iconColor: 'blue',
          title: 'usecases.supplyChain.software.docApp.title',
          description: 'usecases.supplyChain.software.docApp.description',
          status: 'usecases.status.inDevelopment',
          screenshots: [
            {
              src: 'assets/images/Doc App Screenshot 1.png',
              caption: 'usecases.supplyChain.software.docApp.screenshot1'
            },
            {
              src: 'assets/images/Doc App Screenshot 2.png',
              caption: 'usecases.supplyChain.software.docApp.screenshot2'
            }
          ],
          groupKey: 'usecases.supplyChain.softwareGroups.consumer'
        },
        {
          icon: 'settings_ethernet',
          iconColor: 'green',
          title: 'usecases.supplyChain.software.connectorManagement.title',
          description: 'usecases.supplyChain.software.connectorManagement.description',
          status: 'usecases.status.inDevelopment',
          screenshots: [
            {
              src: 'assets/images/Connector Management App Screenshot.png',
              caption: 'usecases.supplyChain.software.connectorManagement.screenshot1'
            }
          ],
          groupKey: 'usecases.supplyChain.softwareGroups.shared'
        }
      ],
      documents: [
        {
          titleKey: 'usecases.supplyChain.documents.terms',
          url: 'assets/pdfs/HT-X-UC1-Nutzungsbedingungen-Datensaetze-v1.0.pdf',
          filename: 'HT-X-UC1-Nutzungsbedingungen-Datensaetze-v1.0.pdf'
        },
        {
          titleKey: 'usecases.supplyChain.documents.dataset',
          url: 'assets/pdfs/HT-X-UC1-Anhang_1-Datensatzbeschreibung.pdf',
          filename: 'HT-X-UC1-Anhang_1-Datensatzbeschreibung.pdf'
        }
      ],
      prerequisites: [
        'usecases.supplyChain.prerequisites.1',
        'usecases.supplyChain.prerequisites.2',
        'usecases.supplyChain.prerequisites.3',
        'usecases.supplyChain.prerequisites.4',
        'usecases.supplyChain.prerequisites.5'
      ],
      faq: [
        {
          question: 'usecases.supplyChain.faq.1.question',
          answer: 'usecases.supplyChain.faq.1.answer'
        },
        {
          question: 'usecases.supplyChain.faq.2.question',
          answer: 'usecases.supplyChain.faq.2.answer'
        },
        {
          question: 'usecases.supplyChain.faq.3.question',
          answer: 'usecases.supplyChain.faq.3.answer'
        },
        {
          question: 'usecases.supplyChain.faq.4.question',
          answer: 'usecases.supplyChain.faq.4.answer'
        },
        {
          question: 'usecases.supplyChain.faq.5.question',
          answer: 'usecases.supplyChain.faq.5.answer'
        },
        {
          question: 'usecases.shared.faq.registration.question',
          answer: 'usecases.shared.faq.registration.answer'
        }
      ]
    },
    'shortage-management': {
      id: 'shortage-management',
      icon: 'monitoring',
      titleKey: 'usecases.shortageManagement.title',
      subtitleKey: 'usecases.shortageManagement.subtitle',
      rolesCount: 3,
      componentsCount: 3,
      descriptionKey: 'usecases.shortageManagement.detailDescription',
      diagram: {
        src: 'assets/images/UC 6-1 Diagramm 0.2.0.svg',
        caption: 'usecases.shortageManagement.diagram.caption'
      },
      roles: [
        {
          icon: 'factory',
          title: 'usecases.shortageManagement.roles.manufacturer.title',
          description: 'usecases.shortageManagement.roles.manufacturer.description',
          permissions: [
            'usecases.shortageManagement.roles.manufacturer.p1',
            'usecases.shortageManagement.roles.manufacturer.p2',
            'usecases.shortageManagement.roles.manufacturer.p3',
            'usecases.shortageManagement.roles.manufacturer.p4'
          ]
        },
        {
          icon: 'local_shipping',
          title: 'usecases.shortageManagement.roles.wholesaler.title',
          description: 'usecases.shortageManagement.roles.wholesaler.description',
          permissions: [
            'usecases.shortageManagement.roles.wholesaler.p1',
            'usecases.shortageManagement.roles.wholesaler.p2',
            'usecases.shortageManagement.roles.wholesaler.p3',
            'usecases.shortageManagement.roles.wholesaler.p4'
          ]
        },
        {
          icon: 'account_balance',
          title: 'usecases.shortageManagement.roles.authority.title',
          description: 'usecases.shortageManagement.roles.authority.description',
          permissions: [
            'usecases.shortageManagement.roles.authority.p1',
            'usecases.shortageManagement.roles.authority.p2',
            'usecases.shortageManagement.roles.authority.p3',
            'usecases.shortageManagement.roles.authority.p4',
            'usecases.shortageManagement.roles.authority.p5'
          ]
        }
      ],
      software: [
        {
          icon: 'assignment',
          iconColor: 'teal',
          title: 'usecases.shortageManagement.software.reportApp.title',
          description: 'usecases.shortageManagement.software.reportApp.description',
          status: 'usecases.status.inDevelopment',
          screenshots: [
            {
              src: 'assets/images/LEP Report App Screenshot 1.png',
              caption: 'usecases.shortageManagement.software.reportApp.screenshot1'
            },
            {
              src: 'assets/images/LEP Report App Screenshot 2.png',
              caption: 'usecases.shortageManagement.software.reportApp.screenshot2'
            }
          ]
        },
        {
          icon: 'settings_ethernet',
          iconColor: 'blue',
          title: 'usecases.shortageManagement.software.connectorManagement.title',
          description: 'usecases.shortageManagement.software.connectorManagement.description',
          status: 'usecases.status.inDevelopment',
          screenshots: [
            {
              src: 'assets/images/Connector Management App Screenshot.png',
              caption: 'usecases.shortageManagement.software.connectorManagement.screenshot1'
            }
          ]
        },
        {
          icon: 'dashboard',
          iconColor: 'green',
          title: 'usecases.shortageManagement.software.lepManagement.title',
          description: 'usecases.shortageManagement.software.lepManagement.description',
          status: 'usecases.status.inDevelopment',
          screenshots: []
        }
      ],
      documents: [
        {
          titleKey: 'usecases.shortageManagement.documents.terms',
          url: 'assets/pdfs/HT-X-UC2.1-Nutzungsbedingungen-Datensaetze-v1.0.pdf',
          filename: 'HT-X-UC2.1-Nutzungsbedingungen-Datensaetze-v1.0.pdf'
        },
        {
          titleKey: 'usecases.shortageManagement.documents.dataset',
          url: 'assets/pdfs/HT-X-UC2.1-Anhang_1-Datensatzbeschreibung.pdf',
          filename: 'HT-X-UC2.1-Anhang_1-Datensatzbeschreibung.pdf'
        }
      ],
      prerequisites: [
        'usecases.shortageManagement.prerequisites.1',
        'usecases.shortageManagement.prerequisites.2',
        'usecases.shortageManagement.prerequisites.3',
        'usecases.shortageManagement.prerequisites.4',
        'usecases.shortageManagement.prerequisites.5'
      ],
      faq: [
        {
          question: 'usecases.shortageManagement.faq.1.question',
          answer: 'usecases.shortageManagement.faq.1.answer'
        },
        {
          question: 'usecases.shortageManagement.faq.2.question',
          answer: 'usecases.shortageManagement.faq.2.answer'
        },
        {
          question: 'usecases.shortageManagement.faq.3.question',
          answer: 'usecases.shortageManagement.faq.3.answer'
        },
        {
          question: 'usecases.shortageManagement.faq.4.question',
          answer: 'usecases.shortageManagement.faq.4.answer'
        },
        {
          question: 'usecases.shortageManagement.faq.5.question',
          answer: 'usecases.shortageManagement.faq.5.answer'
        },
        {
          question: 'usecases.shared.faq.registration.question',
          answer: 'usecases.shared.faq.registration.answer'
        }
      ]
    },
    'product-passport': {
      id: 'product-passport',
      icon: 'co2',
      titleKey: 'usecases.productPassport.title',
      subtitleKey: 'usecases.productPassport.subtitle',
      rolesCount: 3,
      componentsCount: 5,
      descriptionKey: 'usecases.productPassport.detailDescription',
      diagram: {
        src: 'assets/images/UC3-Wertschoepfungskette-Diagramm.svg',
        caption: 'usecases.productPassport.diagram.caption'
      },
      roles: [
        {
          icon: 'category',
          title: 'usecases.productPassport.roles.rawMaterial.title',
          description: 'usecases.productPassport.roles.rawMaterial.description',
          permissions: []
        },
        {
          icon: 'precision_manufacturing',
          title: 'usecases.productPassport.roles.semiFinished.title',
          description: 'usecases.productPassport.roles.semiFinished.description',
          permissions: []
        },
        {
          icon: 'inventory_2',
          title: 'usecases.productPassport.roles.manufacturer.title',
          description: 'usecases.productPassport.roles.manufacturer.description',
          permissions: []
        }
      ],
      software: [
        {
          icon: 'inventory_2',
          iconColor: 'teal',
          title: 'usecases.productPassport.software.productData.title',
          description: 'usecases.productPassport.software.productData.description',
          status: 'usecases.status.inDevelopment',
          screenshots: [
            {
              src: 'assets/images/UC3-Rohstoffdaten-ROH.png',
              caption: 'usecases.productPassport.software.productData.screenshot1'
            },
            {
              src: 'assets/images/UC3-Repository-Graph-Versionierung.png',
              caption: 'usecases.productPassport.software.productData.screenshot2'
            }
          ]
        },
        {
          icon: 'account_tree',
          iconColor: 'blue',
          title: 'usecases.productPassport.software.bom.title',
          description: 'usecases.productPassport.software.bom.description',
          status: 'usecases.status.inDevelopment',
          screenshots: [
            {
              src: 'assets/images/UC3-BOM-Viewer-FERT.png',
              caption: 'usecases.productPassport.software.bom.screenshot1'
            },
            {
              src: 'assets/images/UC3-Lieferkette-Karte.png',
              caption: 'usecases.productPassport.software.bom.screenshot2'
            }
          ]
        },
        {
          icon: 'badge',
          iconColor: 'green',
          title: 'usecases.productPassport.software.passport.title',
          description: 'usecases.productPassport.software.passport.description',
          status: 'usecases.status.inDevelopment',
          screenshots: [
            {
              src: 'assets/images/UC3-Produktuebersicht-Standards.png',
              caption: 'usecases.productPassport.software.passport.screenshot1'
            }
          ]
        },
        {
          icon: 'smart_toy',
          iconColor: 'teal',
          title: 'usecases.productPassport.software.aiClassification.title',
          description: 'usecases.productPassport.software.aiClassification.description',
          status: 'usecases.status.inDevelopment',
          screenshots: [
            {
              src: 'assets/images/UC3-Halbzeug-HALB-CRMA.png',
              caption: 'usecases.productPassport.software.aiClassification.screenshot1'
            },
            {
              src: 'assets/images/UC3-Datenkatalog-Schema.png',
              caption: 'usecases.productPassport.software.aiClassification.screenshot2'
            }
          ]
        },
        {
          icon: 'hub',
          iconColor: 'blue',
          title: 'usecases.productPassport.software.interoperability.title',
          description: 'usecases.productPassport.software.interoperability.description',
          status: 'usecases.status.inDevelopment',
          screenshots: [
            {
              src: 'assets/images/UC3-EDC-Connector.png',
              caption: 'usecases.productPassport.software.interoperability.screenshot1'
            },
            {
              src: 'assets/images/UC3-Overview-Lifecycle.png',
              caption: 'usecases.productPassport.software.interoperability.screenshot2'
            }
          ]
        }
      ],
      documents: [
        {
          titleKey: 'usecases.productPassport.documents.terms',
          url: 'assets/pdfs/HT-X-UC3-Nutzungsbedingungen-Datensaetze-v1.0.pdf',
          filename: 'HT-X-UC3-Nutzungsbedingungen-Datensaetze-v1.0.pdf'
        },
        {
          titleKey: 'usecases.productPassport.documents.dataset',
          url: 'assets/pdfs/HT-X-UC3-Anhang_1-Datensatzbeschreibung.pdf',
          filename: 'HT-X-UC3-Anhang_1-Datensatzbeschreibung.pdf'
        }
      ],
      prerequisites: [
        'usecases.productPassport.prerequisites.1',
        'usecases.productPassport.prerequisites.2',
        'usecases.productPassport.prerequisites.3',
        'usecases.productPassport.prerequisites.4',
        'usecases.productPassport.prerequisites.5'
      ],
      faq: [
        {
          question: 'usecases.productPassport.faq.1.question',
          answer: 'usecases.productPassport.faq.1.answer'
        },
        {
          question: 'usecases.productPassport.faq.2.question',
          answer: 'usecases.productPassport.faq.2.answer'
        },
        {
          question: 'usecases.productPassport.faq.3.question',
          answer: 'usecases.productPassport.faq.3.answer'
        },
        {
          question: 'usecases.productPassport.faq.4.question',
          answer: 'usecases.productPassport.faq.4.answer'
        },
        {
          question: 'usecases.productPassport.faq.5.question',
          answer: 'usecases.productPassport.faq.5.answer'
        },
        {
          question: 'usecases.shared.faq.registration.question',
          answer: 'usecases.shared.faq.registration.answer'
        }
      ]
    }
  };

  useCase = computed(() => this.useCases[this.useCaseId()] || null);

  private t(key: string): string {
    return this.transloco.translate(key);
  }

  heroMetaItems = computed<HeroMetaItem[]>(() => {
    this.translation();
    const uc = this.useCase();
    if (!uc) return [];
    return [
      { icon: 'groups', text: `${uc.rolesCount} ${this.t('usecases.meta.roles')}` },
      { icon: 'apps', text: `${uc.componentsCount} ${this.t('usecases.meta.components')}` },
      { icon: 'verified', text: this.t('usecases.status.active') }
    ];
  });

  pageNavItems = computed<PageNavItem[]>(() => {
    this.translation();
    const uc = this.useCase();
    const items: PageNavItem[] = [
      {
        icon: 'description',
        label: this.t('usecases.sections.description'),
        targetId: 'beschreibung'
      },
      { icon: 'people', label: this.t('usecases.sections.roles'), targetId: 'rollen' },
      {
        icon: 'integration_instructions',
        label: this.t('usecases.sections.software'),
        targetId: 'software'
      }
    ];
    if (uc?.diagram) {
      items.push({
        icon: 'schema',
        label: this.t('usecases.sections.diagram'),
        targetId: 'diagramm'
      });
    }
    if (uc && uc.documents.length > 0) {
      items.push({
        icon: 'description',
        label: this.t('usecases.sections.documents'),
        targetId: 'dokumente'
      });
    }
    items.push(
      {
        icon: 'description',
        label: this.t('usecases.sections.documents'),
        targetId: 'dokumente'
      },
      {
        icon: 'checklist',
        label: this.t('usecases.sections.prerequisites'),
        targetId: 'voraussetzungen'
      },
      { icon: 'help_outline', label: this.t('usecases.sections.faq'), targetId: 'faq' }
    );
    return items;
  });

  roleCards = computed<InfoCard[]>(() => {
    this.translation();
    const uc = this.useCase();
    if (!uc) return [];
    return uc.roles.map((role) => ({
      icon: role.icon,
      title: this.t(role.title),
      description: this.t(role.description),
      tags: role.permissions.map((p) => this.t(p))
    }));
  });

  prerequisiteItems = computed<ChecklistItem[]>(() => {
    this.translation();
    const uc = this.useCase();
    if (!uc) return [];
    return uc.prerequisites.map((p) => ({ text: this.t(p) }));
  });

  faqItems = computed<LibFaqItem[]>(() => {
    this.translation();
    const uc = this.useCase();
    if (!uc) return [];
    return uc.faq.map((f) => ({
      question: this.t(f.question),
      answer: this.t(f.answer)
    }));
  });

  ngOnInit(): void {
    // Set useCaseId from route
    this.subscriptions.add(
      this.route.params.subscribe((params) => {
        this.useCaseId.set(params['id'] || '');
      })
    );

    // Wait for both route params and auth to be ready
    this.subscriptions.add(
      combineLatest([
        this.route.params,
        this.isLoggedIn.pipe(
          filter((v) => v),
          take(1)
        )
      ]).subscribe(([params]) => {
        this.useCaseId.set(params['id'] || '');
        this.statusLoading.set(true);
        this.loadOrgStatus();
        this.loadParticipationStatus();
      })
    );

    // If user is not logged in after initial check, stop loading
    this.subscriptions.add(
      this.isLoggedIn.pipe(take(1)).subscribe((loggedIn) => {
        if (!loggedIn) {
          this.statusLoading.set(false);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadOrgStatus(): void {
    this.organizationService.getMyOrganization().subscribe({
      next: (org) => {
        this.orgNotApproved.set(org.admissionState !== AdmissionState.APPROVED);
        this.orgInactive.set(org.activityState === ActivityState.INACTIVE);
      },
      error: () => this.orgNotApproved.set(true)
    });
  }

  private loadParticipationStatus(): void {
    if (!this.useCaseId()) {
      this.statusLoading.set(false);
      return;
    }
    this.participationService.getMyStatus(this.useCaseId()).subscribe({
      next: (status) => {
        this.participationState.set(status?.admissionState ?? null);
        this.statusError.set(false);
        this.statusLoading.set(false);
      },
      error: () => {
        this.statusError.set(true);
        this.statusLoading.set(false);
      }
    });
  }

  scrollTo(targetId: string): void {
    document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  openLightbox(src: string, captionKey: string): void {
    this.lightboxSrc.set(src);
    this.lightboxAlt.set(this.transloco.translate(captionKey));
  }

  closeLightbox(): void {
    this.lightboxSrc.set(null);
  }

  onRequestParticipation(): void {
    if (this.requestInProgress() || this.participationState()) return;
    this.requestInProgress.set(true);

    this.participationService.requestParticipation(this.useCaseId()).subscribe({
      next: () => {
        this.participationState.set(AdmissionState.PENDING);
        this.notificationService.showSuccess('usecases.cta.success');
        this.requestInProgress.set(false);
      },
      error: () => {
        this.notificationService.showError('usecases.cta.error');
        this.requestInProgress.set(false);
      }
    });
  }
}
